import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../../lib/cn'

type Variant = 'ink' | 'accent' | 'approve' | 'outline' | 'ghost'
type Size = 'sm' | 'md'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
}

const variantStyles: Record<Variant, string> = {
  ink: 'bg-ink text-paper-panel hover:bg-ink/90',
  accent: 'bg-terra text-paper-surface hover:bg-terra-deep',
  approve: 'bg-moss text-paper-surface hover:bg-moss/90',
  outline: 'border border-line bg-transparent text-ink-muted hover:border-terra hover:text-terra-deep',
  ghost: 'bg-transparent text-terra-deep hover:text-terra-hover',
}

const sizeStyles: Record<Size, string> = {
  sm: 'px-[14px] py-[9px] text-[13px]',
  md: 'px-[18px] py-3 text-[13.5px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'ink', size = 'md', type = 'button', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-btn font-sans font-medium leading-none transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terra/40',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)
Button.displayName = 'Button'
